import React, { useMemo } from 'react';
import {
  Shield, Target, TrendingUp, Brain, AlertTriangle, Eye, ChevronDown
} from 'lucide-react';
import { LLMDecision } from '../../types/api';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { DecisionCard } from './DecisionCard';

interface RiskManagementCardProps {
  decision: LLMDecision;
  isLatest: boolean;
}

export const RiskManagementCard: React.FC<RiskManagementCardProps> = ({
  decision,
  isLatest,
}) => {
  const rec = decision.recommendation;

  // Memoize JSON stringification to avoid re-computing on every render
  const jsonData = useMemo(() => {
    return JSON.stringify(rec, null, 2);
  }, [rec]);

  if (!rec) return null;

  return (
    <DecisionCard decision={decision} isLatest={isLatest}>
      <CardContent className="space-y-4">
        {/* Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Stop Loss */}
          <Card className="bg-gradient-to-br from-red-50 to-red-100/50 dark:from-red-950/20 dark:to-red-900/10">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <Shield className="h-3 w-3 text-red-600" />
                <span className="text-xs font-medium text-red-600">Stop Loss</span>
              </div>
              <p className="text-lg font-bold">
                {rec.stop_loss_percentage?.toFixed(2) || 'N/A'}%
              </p>
              {rec.stop_loss_price && (
                <p className="text-xs text-muted-foreground">
                  ${rec.stop_loss_price.toFixed(2)}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Take Profit */}
          <Card className="bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-950/20 dark:to-green-900/10">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <Target className="h-3 w-3 text-green-600" />
                <span className="text-xs font-medium text-green-600">Take Profit</span>
              </div>
              <p className="text-lg font-bold">
                {rec.take_profit_percentage?.toFixed(2) || 'N/A'}%
              </p>
              {rec.take_profit_price && (
                <p className="text-xs text-muted-foreground">
                  ${rec.take_profit_price.toFixed(2)}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Trailing Stop */}
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-950/20 dark:to-blue-900/10">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-3 w-3 text-blue-600" />
                <span className="text-xs font-medium text-blue-600">Trailing Stop</span>
              </div>
              <p className="text-lg font-bold">
                {rec.trailing_stop_percentage?.toFixed(2) || 'N/A'}%
              </p>
              <p className="text-xs text-muted-foreground">
                {rec.trailing_stop_activation || 'after_tp'}
              </p>
            </CardContent>
          </Card>

          {/* Confidence */}
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100/50 dark:from-purple-950/20 dark:to-purple-900/10">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <Brain className="h-3 w-3 text-purple-600" />
                <span className="text-xs font-medium text-purple-600">Confidence</span>
              </div>
              <p className="text-lg font-bold">
                {rec.confidence_score || 'N/A'}/100
              </p>
              <p className="text-xs text-muted-foreground">Agent Score</p>
            </CardContent>
          </Card>
        </div>

        {/* Risk Factors */}
        {rec.risk_factors && rec.risk_factors.length > 0 && (
          <div>
            <h4 className="font-medium mb-2 flex items-center gap-2 text-sm">
              <AlertTriangle className="h-3 w-3" />
              Risk Factors Identified
            </h4>
            <div className="flex flex-wrap gap-2">
              {rec.risk_factors.map((factor: string, idx: number) => (
                <Badge key={idx} variant="outline" className="text-xs">
                  {factor}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Full Data - Collapsible */}
        <details className="cursor-pointer group">
          <summary className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
            <Eye className="h-3 w-3" />
            <span>Show Full Decision Data</span>
            <ChevronDown className="h-3 w-3 transition-transform group-open:rotate-180" />
          </summary>
          <Card className="bg-muted/30 mt-2">
            <CardContent className="p-3">
              <div className="max-h-32 overflow-y-auto">
                <pre className="text-xs font-mono whitespace-pre-wrap break-words">
                  {jsonData}
                </pre>
              </div>
            </CardContent>
          </Card>
        </details>
      </CardContent>
    </DecisionCard>
  );
};

export default RiskManagementCard;
